// パスキー（WebAuthn）のクライアント側。docs/AUTH.md の実装順序(3)。
//
// ブラウザ API との受け渡しは **ArrayBuffer**、サーバーとの受け渡しは **base64url** なので、
// その変換を一手に引き受ける。ここを画面側に散らすと、
// 「なぜか登録できない」の原因が毎回エンコード違いになる。
//
// ⚠️ **`navigator.credentials` は安全なオリジンでしか使えない**（https か localhost）。
//    それ以外では `isPasskeySupported()` が false を返すので、UI を出さないこと。

import type { LoggedInPlayer } from '@/platform/types/session';

const API_BASE = import.meta.env.VITE_API_BASE ?? '/api';

// ------------------------------------------------------------------ base64url
function toBase64Url(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * `ArrayBuffer` で返す（`Uint8Array` ではなく）。WebAuthn の `BufferSource` は
 * `SharedArrayBuffer` を除く必要があり、`Uint8Array` のままだと型が通らない。
 */
function fromBase64Url(s: string): ArrayBuffer {
  const b64 = s.replace(/-/g, '+').replace(/_/g, '/');
  const bin = atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4));
  const buf = new ArrayBuffer(bin.length);
  const view = new Uint8Array(buf);
  for (let i = 0; i < bin.length; i++) view[i] = bin.charCodeAt(i);
  return buf;
}

// ------------------------------------------------------------------ 対応判定
/** この環境でパスキーが使えるか（安全なオリジンかどうかも含む）。 */
export function isPasskeySupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.isSecureContext === true &&
    typeof window.PublicKeyCredential === 'function' &&
    typeof navigator.credentials?.create === 'function'
  );
}

/**
 * この端末に**内蔵の認証器**（Face ID / Touch ID / Windows Hello）があるか。
 * 無い場合でも QR のクロスデバイスやセキュリティキーで登録はできるので、
 * **これが false でも登録ボタンを隠さない**。文言を変える程度の判断に使う。
 */
export async function hasPlatformAuthenticator(): Promise<boolean> {
  if (!isPasskeySupported()) return false;
  try {
    return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

// ------------------------------------------------------------------ 登録
export type PasskeyRegisterResult =
  | { ok: true; label: string | null }
  /**
   * `cancelled` は**利用者が自分でやめた**場合（生体認証のシートを閉じた等）。
   * ⚠️ エラー表示を出さないこと。「やめた」に赤いダイアログを出すのは驚かせるだけ。
   *
   * 🔴 `already` は**この端末に既に登録がある**場合（2026-08-08 の本番実機テストで発覚）。
   *    `failed` に混ぜてはいけない。混ぜると「時間をおいてもう一度お試しください」と出て、
   *    **永久に成功しない再試行を促す**ことになる（恒久的な状態を一時的な障害に見せてしまう）。
   */
  | { ok: false; reason: 'unsupported' | 'cancelled' | 'already' | 'failed'; message?: string };

interface RegisterOptionsResponse {
  challenge: string;
  rp: { id: string; name: string };
  user: { id: string; name: string; displayName: string };
  pubKeyCredParams: { type: 'public-key'; alg: number }[];
  excludeCredentials: { type: 'public-key'; id: string; transports?: string[] }[];
}

/**
 * この端末にパスキーを1つ登録する。
 *
 * 成功するとサーバーがセッション Cookie を返すので、**この端末はログイン済みになる**。
 */
export async function registerPasskey(
  playerId: string,
  label?: string,
): Promise<PasskeyRegisterResult> {
  if (!isPasskeySupported()) return { ok: false, reason: 'unsupported' };

  let options: RegisterOptionsResponse;
  try {
    const res = await fetch(`${API_BASE}/passkey/register-options`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ playerId }),
    });
    if (!res.ok) return { ok: false, reason: 'failed', message: `options ${res.status}` };
    options = await res.json();
  } catch {
    return { ok: false, reason: 'failed', message: 'network' };
  }

  let credential: PublicKeyCredential | null;
  try {
    credential = (await navigator.credentials.create({
      publicKey: {
        challenge: fromBase64Url(options.challenge),
        rp: options.rp,
        user: {
          // サーバーは player_id を文字列で渡してくる。UTF-8 バイト列にして戻す
          // （ログイン時に userHandle として返ってくるので、同じ規則で読む）。
          id: new TextEncoder().encode(options.user.id),
          name: options.user.name,
          displayName: options.user.displayName,
        },
        pubKeyCredParams: options.pubKeyCredParams,
        excludeCredentials: options.excludeCredentials.map((c) => ({
          type: 'public-key' as const,
          id: fromBase64Url(c.id),
          transports: c.transports as AuthenticatorTransport[] | undefined,
        })),
        authenticatorSelection: {
          // 🔴 **discoverable credential にする**（ID 入力なしの1タップの前提）。
          residentKey: 'preferred',
          // ⚠️ `required` にしない。生体が無い端末を締め出さないため
          //    （サーバー側も UV を必須にしていない）。
          userVerification: 'preferred',
        },
        // 出自の証明は要らない（docs/AUTH.md 参照）。取らなければ扱う個人情報も増えない。
        attestation: 'none',
        timeout: 120_000,
      },
    })) as PublicKeyCredential | null;
  } catch (e) {
    const name = e instanceof DOMException ? e.name : '';
    if (name === 'NotAllowedError' || name === 'AbortError') {
      return { ok: false, reason: 'cancelled' };
    }
    // `excludeCredentials` に載っている鍵を認証器が既に持っていると `InvalidStateError`。
    // ＝「この端末はもう登録済み」という**正常な結果**であって、障害ではない。
    if (name === 'InvalidStateError') return { ok: false, reason: 'already' };
    return { ok: false, reason: 'failed', message: name || 'create failed' };
  }
  if (!credential) return { ok: false, reason: 'cancelled' };

  const response = credential.response as AuthenticatorAttestationResponse;
  try {
    const res = await fetch(`${API_BASE}/passkey/register`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        playerId,
        label: label ?? deviceLabel(),
        response: {
          clientDataJSON: toBase64Url(response.clientDataJSON),
          attestationObject: toBase64Url(response.attestationObject),
          transports: response.getTransports?.() ?? undefined,
        },
      }),
    });
    if (!res.ok) return { ok: false, reason: 'failed', message: `register ${res.status}` };
    const body = (await res.json()) as { registered?: boolean; label?: string | null };
    return body.registered ? { ok: true, label: body.label ?? null } : { ok: false, reason: 'failed' };
  } catch {
    return { ok: false, reason: 'failed', message: 'network' };
  }
}

// ------------------------------------------------------------------ ログイン
// 型は [types/session.ts](../types/session.ts) にある（引き継ぎコードの入口と共有するため）。
export type { LoggedInPlayer };

export type PasskeyLoginResult =
  | { ok: true; player: LoggedInPlayer }
  | { ok: false; reason: 'unsupported' | 'cancelled' | 'failed'; message?: string };

/**
 * パスキーでログインする。**ID の入力は要らない**
 * （登録時に discoverable credential にしてあるので、認証器が持ち主を知っている）。
 *
 * ⚠️ **この関数は localStorage を書き換えない**。誰としてログインしたかを返すだけにして、
 *    「この端末に残っている記録をどうするか」の判断を呼び出し側に残す
 *    （統合は docs/AUTH.md の実装順序(5)）。ここで勝手に持ち主を差し替えると、
 *    前の持ち主の記録が新しいプレイヤーのものとして同期されうる。
 */
export async function loginWithPasskey(): Promise<PasskeyLoginResult> {
  if (!isPasskeySupported() || typeof navigator.credentials.get !== 'function') {
    return { ok: false, reason: 'unsupported' };
  }

  let options: { challenge: string; rpId: string; userVerification: UserVerificationRequirement };
  try {
    const res = await fetch(`${API_BASE}/passkey/login-options`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'same-origin',
      body: '{}',
    });
    if (!res.ok) return { ok: false, reason: 'failed', message: `options ${res.status}` };
    options = await res.json();
  } catch {
    return { ok: false, reason: 'failed', message: 'network' };
  }

  let credential: PublicKeyCredential | null;
  try {
    credential = (await navigator.credentials.get({
      publicKey: {
        challenge: fromBase64Url(options.challenge),
        rpId: options.rpId,
        // 🔴 **空にする**。ここに候補を入れると「誰か」を先に決める必要が出てしまい、
        //    ID 入力なしの1タップにならない。
        allowCredentials: [],
        userVerification: options.userVerification,
        timeout: 120_000,
      },
    })) as PublicKeyCredential | null;
  } catch (e) {
    const name = e instanceof DOMException ? e.name : '';
    if (name === 'NotAllowedError' || name === 'AbortError') {
      return { ok: false, reason: 'cancelled' };
    }
    return { ok: false, reason: 'failed', message: name || 'get failed' };
  }
  if (!credential) return { ok: false, reason: 'cancelled' };

  const response = credential.response as AuthenticatorAssertionResponse;
  try {
    const res = await fetch(`${API_BASE}/passkey/login`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        id: credential.id,
        response: {
          clientDataJSON: toBase64Url(response.clientDataJSON),
          authenticatorData: toBase64Url(response.authenticatorData),
          signature: toBase64Url(response.signature),
        },
      }),
    });
    if (!res.ok) return { ok: false, reason: 'failed', message: `login ${res.status}` };
    const body = (await res.json()) as { player?: LoggedInPlayer };
    return body.player ? { ok: true, player: body.player } : { ok: false, reason: 'failed' };
  } catch {
    return { ok: false, reason: 'failed', message: 'network' };
  }
}

/**
 * 端末の呼び名の既定値。**UA 文字列をそのまま出さない**
 * （長すぎて画面に収まらないし、利用者にとって意味のある情報でもない）。
 */
export function deviceLabel(
  ua: string = navigator.userAgent,
  touchPoints: number = navigator.maxTouchPoints,
): string {
  if (/iPad/.test(ua)) return 'iPad';
  if (/iPhone/.test(ua)) return 'iPhone';
  if (/Android/.test(ua)) return 'Android';
  // 🔴 **iPad は UA だけでは Mac と見分けられない**（2026-08-08 の本番実機テストで発覚）。
  //    iPadOS の Safari は「デスクトップ用サイトを表示」が既定で有効なので、
  //    `Macintosh; Intel Mac OS X` を名乗り、上の `/iPad/` に当たらない。
  //    タッチ点数で分ける（Mac は 0、iPad は 5）。**ここを UA の追加パターンで直そうとしない**こと
  //    ——Apple が意図的に同じ文字列を送っているので、UA 側には手がかりが無い。
  if (/Mac OS X|Macintosh/.test(ua)) {
    return touchPoints > 1 ? 'iPad' : 'Mac';
  }
  if (/Windows/.test(ua)) return 'Windows';
  return 'この端末';
}
